import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Download, Bookmark, ShieldAlert, Award } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

function download(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Library() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/saved").then(({ data }) => {
      setItems(data.items || []);
      setActive((prev) => prev || (data.items && data.items[0]) || null);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/saved/${id}`);
      const next = items.filter((x) => x.id !== id);
      setItems(next);
      if (active?.id === id) setActive(next[0] || null);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed", { description: apiErr(e) });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <header className="h-14 border-b border-border bg-white flex items-center justify-between px-5 shrink-0">
        <div className="flex items-center gap-3">
          <Button data-testid="library-back" variant="ghost" className="rounded-none font-mono text-xs" onClick={() => navigate("/app")}>
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" /> Dashboard
          </Button>
          <span className="font-heading font-extrabold tracking-tight flex items-center gap-2">
            <Bookmark className="w-4 h-4" aria-hidden="true" /> My Library
          </span>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">{items.length} saved</span>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        {/* list */}
        <div className="lg:col-span-4 xl:col-span-3 border-r border-border bg-white overflow-y-auto">
          {loading ? (
            <p className="p-5 font-mono text-xs text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bookmark className="w-6 h-6 mx-auto mb-3" aria-hidden="true" />
              <p className="text-sm">No saved articles yet. Generate an article and hit Save.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((it) => {
                const flagged = it.authenticity?.score != null && it.authenticity.score < 90;
                return (
                  <button
                    key={it.id}
                    data-testid={`library-item-${it.id}`}
                    onClick={() => setActive(it)}
                    className={`w-full text-left p-4 transition-colors border-l-4 ${active?.id === it.id ? "bg-[#FFF9E0] border-l-[#ffce00]" : "border-l-transparent hover:bg-secondary"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{it.language}</span>
                      {it.eeat && <span className="font-mono text-[10px] bg-[#111110] text-[#ffce00] px-1.5">{it.eeat.overall_pct}%</span>}
                      {flagged && <ShieldAlert className="w-3 h-3 text-[#B3232F]" aria-hidden="true" />}
                    </div>
                    <p className="font-heading font-bold text-sm leading-snug line-clamp-2">{it.title}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* detail */}
        <div className="lg:col-span-8 xl:col-span-9 overflow-y-auto p-6" data-testid="library-detail">
          {!active ? (
            <p className="font-mono text-xs text-muted-foreground">Select an article.</p>
          ) : (
            <div className="max-w-3xl">
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1 className="font-heading font-extrabold text-2xl tracking-tight">{active.title}</h1>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => download(`${active.seo?.slug || "article"}.txt`, active.article)}>
                    <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> TXT
                  </Button>
                  <Button data-testid="library-delete" size="sm" variant="outline" className="rounded-none h-8 text-[#B3232F] border-[#B3232F]" onClick={() => remove(active.id)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Delete
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {active.authenticity?.score != null && (
                  <span className={`font-mono text-[11px] px-2 py-1 ${active.authenticity.score < 90 ? "bg-[#FDECEC] text-[#B3232F]" : "bg-[#F4F7EE] text-[#3B5323]"}`}>
                    Authenticity {active.authenticity.score}%
                  </span>
                )}
                {active.eeat && (
                  <span className="font-mono text-[11px] px-2 py-1 bg-[#111110] text-[#ffce00] flex items-center gap-1">
                    <Award className="w-3 h-3" aria-hidden="true" /> E-E-A-T {active.eeat.overall_pct}%
                  </span>
                )}
                {active.source && (
                  active.link ? (
                    <a href={active.link} target="_blank" rel="noopener noreferrer"
                      data-testid="library-source-link"
                      className="font-mono text-[11px] px-2 py-1 bg-secondary hover:bg-[#ffce00] transition-colors">
                      Source: {active.source}{(active.source_via || active.link.includes("news.google.com")) ? " (via Google News)" : ""} ↗
                    </a>
                  ) : (
                    <span className="font-mono text-[11px] px-2 py-1 bg-secondary">{active.source}</span>
                  )
                )}
              </div>

              {active.image_data_url && (
                <img src={active.image_data_url} alt="" className="w-full border border-border mb-5" />
              )}

              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{active.article}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
